import { useHistory } from "react-router";
import {
    AddNewIcon,
    AIFActiveIcon,
    AIFIcon,
    BondsActiveIcon,
    BondsIcon,
    DashboardInactiveIcon,
    DirectEquityActiveIcon,
    DirectEquityIcon,
    DownloadIcon,
    MutualFundsIcon,
    PaymentsActiveIcon,
    PaymentsInactiveIcon,
    PMSActiveIcon,
    PMSIcon,
    SidebarDashboardIcon,
    ViewEmailConsentIcon,
    ViewExistingIcon,
} from "./customSVGs";
import {
    Dispatch,
    SetStateAction,
    useMemo
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { getParams } from "../redux-store/actions/application";
import { initialState } from "../redux-store/reducers/queryParams";
import {
    ActiveAddNewIcon,
    ActiveDownloadIcon,
    ActiveViewEmailConsentIcon,
    ActiveViewExistingIcon,
    InvestorActiveReportsIcon,
    InvestorMFActiveIcon,
    InvestorReportsIcon,
    RecommendationsIcon,


} from "../assets/SvgIcons";
import { RootStateType } from "../redux-store/reducers";
import {
    ENABLE_ORDER_MANAGEMENT,
    ENABLE_PAYMENT_CHECKER,
    SUBROLE,
    USER_ROLES
} from "../utils/constant";
import {
    setAppliedRecommendationsFilters,
    setRecommendationsActiveCategories
} from "../redux-store/actions/investorProfile";
import { isModuleAllowed } from "../utils/utilityFunctions";
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';

interface MenuItem {
    name: string;
    activeIcon?: JSX.Element;
    inActiveIcon?: JSX.Element;
    handleClick: () => void;
    route?: Array<string>;
    dropdownId?: string;
    isDisabled: boolean;
}

interface SidebarItem {
    isSubMenuItem: boolean;
    menuItem: MenuItem;
    subMenuItem: MenuItem[];
}

export default function getSideBarData({
    activeCategory,
    setActiveCategory
}: {
    activeCategory: string;
    setActiveCategory: Dispatch<SetStateAction<string>>;
}): Array<SidebarItem> {
    const history = useHistory();
    const dispatch = useDispatch();

    const {
        role = '',
        subRole = "",
        permissions = []
    } = useSelector((store: RootStateType) => store.auth);

    const { investorClientId = "" } = useSelector((store: RootStateType) => store.id)

    const sideBarData = useMemo(() => {
        return (
            [

                //   NON INVESTOR DASHBOARD
                ...((
                    ![USER_ROLES.INVESTOR].includes(role) &&
                    isModuleAllowed({ value: "MFDashboard", compareArr: permissions, objKey: "permissonCode" })
                ) ? [{
                    isSubMenuItem: false,
                    menuItem: {
                        name: 'Dashboard',
                        activeIcon: <SidebarDashboardIcon />,
                        inActiveIcon: <DashboardInactiveIcon />,
                        handleClick: () => history.push('/dashboard'),
                        route: ['/dashboard'],
                        isDisabled: false
                    },
                    subMenuItem: [],
                }] : []),

                // INVESTOR DASHBOARD
                ...(([USER_ROLES.INVESTOR].includes(role) && isModuleAllowed({ value: "INVDashboard", compareArr: permissions, objKey: "permissonCode" })) ? [{
                    isSubMenuItem: false,
                    menuItem: {
                        name: 'Dashboard',
                        activeIcon: <SidebarDashboardIcon />,
                        inActiveIcon: <DashboardInactiveIcon />,
                        handleClick: () => history.push('/investor-dashboard'),
                        route: ['/investor-dashboard'],
                        isDisabled: false
                    },
                    subMenuItem: [],
                }] : []),

                // INVESTOR RECOMMENDATIONS
                ...([USER_ROLES.INVESTOR].includes(role) ? [{
                    isSubMenuItem: false,
                    menuItem: {
                        name: 'Suggestions',
                        activeIcon: <RecommendationsIcon />,
                        inActiveIcon: <RecommendationsIcon />,
                        handleClick: () => {
                            dispatch(setRecommendationsActiveCategories({
                                primaryCategory: "New Purchase",
                                secondaryCategory: "Lumpsum"
                            }))
                            dispatch(setAppliedRecommendationsFilters([]))

                            history.push('/investor-recommendations')
                        },
                        route: ['/investor-recommendations'],
                        isDisabled: false
                    },
                    subMenuItem: [],
                }] : []),

                ...([SUBROLE.SUB_BRK].includes(`${subRole ?? ''}`.toUpperCase() as SUBROLE) ? [{
                    isSubMenuItem: false,
                    menuItem: {
                        name: 'My Profile',
                        activeIcon: <AccountCircleIcon />,
                        inActiveIcon: <AccountCircleOutlinedIcon />,
                        handleClick: () => {
                            history.push('/my-profile')
                        },
                        route: ['/my-profile'],
                        isDisabled: false
                    },
                    subMenuItem: [],
                }] : []),

                // INVESTOR PRODUCTS
                {
                    isSubMenuItem: true,
                    menuItem: {
                        name: 'INVEST',
                        handleClick: () => setActiveCategory(activeCategory === 'invest' ? '' : 'invest'),
                        dropdownId: 'invest',
                        isDisabled: false
                    },
                    subMenuItem: [
                        // INVESTOR MUTUAL FUNDS
                        ...([USER_ROLES.INVESTOR].includes(role) ? [{
                            name: 'Mutual Funds',
                            activeIcon: <InvestorMFActiveIcon />,
                            inActiveIcon: <MutualFundsIcon />,
                            handleClick: () => {
                                history.push('/investor-dashboard/mutual-fund?tab=Overview', {
                                    clientCode: investorClientId ?? ""
                                })
                            },
                            route: ['/investor-dashboard/mutual-fund'],
                            isDisabled: false
                        }] : []),

                        // INVESTOR DIRECT EQUITY
                        ...((
                            [USER_ROLES.INVESTOR].includes(role) &&
                            isModuleAllowed({ value: "DirectEQDashboard", compareArr: permissions, objKey: "permissonCode" })
                        ) ? [{
                            name: 'Direct Equity',
                            activeIcon: <DirectEquityActiveIcon />,
                            inActiveIcon: <DirectEquityIcon />,
                            handleClick: () => {
                                history.push('/investor-dashboard/direct-equity')
                            },
                            route: ["/investor-dashboard/direct-equity"],
                            isDisabled: false
                        }] : []),

                        // INVESTOR PMS
                        ...((
                            [USER_ROLES.INVESTOR].includes(role) &&
                            isModuleAllowed({ value: "PMSDashboard", compareArr: permissions, objKey: "permissonCode" })
                        ) ? [{
                            name: 'PMS',
                            activeIcon: <PMSActiveIcon />,
                            inActiveIcon: <PMSIcon />,
                            handleClick: () => {
                                history.push('/investor-dashboard/pms')
                            },
                            route: ["/investor-dashboard/pms"],
                            isDisabled: false
                        }] : []),

                        // INVESTOR DEBT
                        ...((
                            [USER_ROLES.INVESTOR].includes(role) &&
                            isModuleAllowed({ value: "DebtDashboard", compareArr: permissions, objKey: "permissonCode" })
                        ) ? [{
                            name: 'Debt',
                            activeIcon: <BondsActiveIcon />,
                            inActiveIcon: <BondsIcon />,
                            handleClick: () => {
                                history.push('/investor-dashboard/debt')
                            },
                            route: ["/investor-dashboard/debt"],
                            isDisabled: false
                        }] : []),

                        // INVESTOR AIF
                        ...((
                            [USER_ROLES.INVESTOR].includes(role) &&
                            isModuleAllowed({ value: "AIFDashboard", compareArr: permissions, objKey: "permissonCode" })
                        ) ? [{
                            name: 'Alternates',
                            activeIcon: <AIFActiveIcon />,
                            inActiveIcon: <AIFIcon />,
                            handleClick: () => {
                                history.push('/investor-dashboard/alternates')
                            },
                            route: ["/investor-dashboard/alternates"],
                            isDisabled: false
                        }] : []),

                        // INVESTOR INSURANCE
                        ...((
                            [USER_ROLES.INVESTOR].includes(role) &&
                            isModuleAllowed({ value: "INSDashboard", compareArr: permissions, objKey: "permissonCode" })
                        ) ? [{
                            name: 'Insurance',
                            activeIcon: <DirectEquityActiveIcon />,
                            inActiveIcon: <DirectEquityIcon />,
                            handleClick: () => {
                                history.push('/investor-dashboard/insurance')
                            },
                            route: ["/investor-dashboard/insurance"],
                            isDisabled: false
                        }] : [])
                    ],
                },

                //   NON INVESTOR PRODUCTS
                {
                    isSubMenuItem: true,
                    menuItem: {
                        name: 'PRODUCTS',
                        handleClick: () => setActiveCategory(activeCategory === 'products' ? '' : 'products'),
                        dropdownId: 'products',
                        isDisabled: false
                    },
                    subMenuItem: [
                        // NON INVESTOR MUTUAL FUNDS
                        ...((
                            !([USER_ROLES.INVESTOR].includes(role)) &&
                            (
                                isModuleAllowed({ value: 'MFOrders', compareArr: permissions, objKey: "permissonCode" }) ||
                                isModuleAllowed({ value: 'OrderRecommend', compareArr: permissions, objKey: "permissonCode" })
                            )
                        ) ? [{
                            name: 'Mutual Funds',
                            activeIcon: <InvestorMFActiveIcon />,
                            inActiveIcon: <MutualFundsIcon />,
                            handleClick: () => {
                                history.push('/product/mutual-fund')
                            },
                            route: ['/product/mutual-fund'],
                            isDisabled: false
                        }] : []),

                        // NON INVESTOR DIRECT EQUITY
                        ...((
                            ![USER_ROLES.INVESTOR].includes(role) &&
                            isModuleAllowed({ value: "DirectEQDashboard", compareArr: permissions, objKey: "permissonCode" })
                        ) ? [{
                            name: 'Direct Equity',
                            activeIcon: <DirectEquityActiveIcon />,
                            inActiveIcon: <DirectEquityIcon />,
                            handleClick: () => {
                                history.push('/product/direct-equity')
                            },
                            route: ["/product/direct-equity"],
                            isDisabled: false
                        }] : []),

                        // NON INVESTOR PMS
                        ...((
                            ![USER_ROLES.INVESTOR].includes(role) &&
                            isModuleAllowed({ value: "PMSDashboard", compareArr: permissions, objKey: "permissonCode" })
                        ) ? [{
                            name: 'PMS',
                            activeIcon: <PMSActiveIcon />,
                            inActiveIcon: <PMSIcon />,
                            handleClick: () => {
                                history.push('/product/pms')
                            },
                            route: ["/product/pms"],
                            isDisabled: false
                        }] : []),

                        // NON INVESTOR DEBT
                        ...((
                            ![USER_ROLES.INVESTOR].includes(role) &&
                            isModuleAllowed({ value: "DebtDashboard", compareArr: permissions, objKey: "permissonCode" })
                        ) ? [{
                            name: 'Debt',
                            activeIcon: <BondsActiveIcon />,
                            inActiveIcon: <BondsIcon />,
                            handleClick: () => {
                                history.push('/product/debt')
                            },
                            route: ["/product/debt"],
                            isDisabled: false
                        }] : []),

                        // NON INVESTOR AIF
                        ...((
                            ![USER_ROLES.INVESTOR].includes(role) &&
                            isModuleAllowed({ value: "AIFDashboard", compareArr: permissions, objKey: "permissonCode" })
                        ) ? [{
                            name: 'Alternates',
                            activeIcon: <AIFActiveIcon />,
                            inActiveIcon: <AIFIcon />,
                            handleClick: () => {
                                history.push('/product/alternates')
                            },
                            route: ["/product/alternates"],
                            isDisabled: false
                        }] : []),

                        // NON INVESTOR INSURANCE
                        ...((
                            ![USER_ROLES.INVESTOR].includes(role) &&
                            isModuleAllowed({ value: "INSDashboard", compareArr: permissions, objKey: "permissonCode" })
                        ) ? [{
                            name: 'Insurance',
                            activeIcon: <DirectEquityActiveIcon />,
                            inActiveIcon: <DirectEquityIcon />,
                            handleClick: () => {
                                history.push('/product/insurance')
                            },
                            route: ["/product/insurance"],
                            isDisabled: false
                        }] : [])
                    ],
                },

                //   ONBOARDING APPLICANTS
                {
                    isSubMenuItem: true,
                    menuItem: {
                        name: 'ONBOARDING',
                        handleClick: () => setActiveCategory(activeCategory === 'applications' ? '' : 'applications'),
                        dropdownId: 'applications',
                        isDisabled: false
                    },
                    subMenuItem: [

                        // ONBOARD NEW APPLICANT
                        ...(isModuleAllowed({ value: "INVOnboard", compareArr: permissions, objKey: "permissonCode" }) ?
                            [{
                                name: 'Create Onboarding',
                                activeIcon: <ActiveAddNewIcon />,
                                inActiveIcon: <AddNewIcon />,
                                handleClick: () => {
                                    dispatch(getParams(initialState))
                                    history.push('/onboarding')
                                },
                                route: ['/onboarding/enter-pan-number'],
                                isDisabled: false
                            }] : []),

                        // VIEW EXISTING APPLICANT
                        ...(isModuleAllowed({ value: "APPL", compareArr: permissions, objKey: "permissonCode" }) ? [{
                            name: 'Onboarding Status',
                            activeIcon: <ActiveViewExistingIcon />,
                            inActiveIcon: <ViewExistingIcon />,
                            handleClick: () => {
                                if ([USER_ROLES.AMCAPPROVER].includes(role)) {
                                    history.push('/applications-list')
                                    return
                                } else {
                                    dispatch(getParams(initialState))
                                    history.push('/applications')
                                }
                            },
                            route: [[USER_ROLES.AMCAPPROVER].includes(role) ? "/applications-list" : "/applications"],
                            isDisabled: [USER_ROLES.POACHECKER].includes(subRole)
                        }] : []),

                        ...(isModuleAllowed({ value: "ReKYC", compareArr: permissions, objKey: "permissonCode" }) ?
                            [{
                                name: 'Re-KYC Status',
                                activeIcon: <ActiveViewExistingIcon />,
                                inActiveIcon: <ViewExistingIcon />,
                                handleClick: () => {
                                    dispatch(getParams(initialState))
                                    history.push('/re-kyc-status')
                                },
                                route: ['/re-kyc-status'],
                                isDisabled: false
                            }] : []),

                        ...(isModuleAllowed({ value: "NONMFAPPL", compareArr: permissions, objKey: "permissonCode" }) ?
                            [{
                                name: 'Non MF Product',
                                activeIcon: <ActiveViewExistingIcon />,
                                inActiveIcon: <ViewExistingIcon />,
                                handleClick: () => {
                                    dispatch(getParams(initialState))
                                    history.push('/non-mf-applications')
                                },
                                route: ['/non-mf-applications'],
                                isDisabled: false
                            }] : []),

                        // VIEW EMAIL CONSENT    
                        // ...(isModuleAllowed({ value: "INVOnboard", compareArr: permissions, objKey: "permissonCode" }) ?
                        //     [{
                        //         name: 'View Email Consent',
                        //         activeIcon: <ActiveViewEmailConsentIcon />,
                        //         inActiveIcon: <ViewEmailConsentIcon />,
                        //         handleClick: () => {
                        //             dispatch(getParams(initialState))
                        //             history.push('/view-consent-email-list')
                        //         },
                        //         route: ["/view-consent-email-list"],
                        //         isDisabled: false
                        //     }] : []),

                        // {
                        //     name: 'Onboarding DashBoard',
                        //     activeIcon: <ActiveInvestorOnBoardingIcon />,
                        //     inActiveIcon: <InvestorOnBoardingIcon />,
                        //     handleClick: () => {
                        //         dispatch(getParams(initialState))
                        //         history.push('/onboard-overview')
                        //     },
                        //     route: ["/onboard-overview"],
                        //     isDisabled: false
                        // }
                    ],
                },

                //Joint Holder

                {
                    isSubMenuItem: true,
                    menuItem: {
                        name: 'JOINT ACCOUNT',
                        handleClick: () => setActiveCategory(activeCategory === 'jointHolder' ? '' : 'jointHolder'),
                        dropdownId: 'jointHolder',
                        isDisabled: false
                    },
                    subMenuItem: [
                        // ONBOARD NEW JOINT APPLICANT
                        ...(isModuleAllowed({ value: "JointHolderCreation", compareArr: permissions, objKey: "permissonCode" }) ?
                            [{
                                name: 'Create Joint Account',
                                activeIcon: <ActiveAddNewIcon />,
                                inActiveIcon: <AddNewIcon />,
                                handleClick: () => {
                                    dispatch(getParams(initialState))
                                    history.push('/create-account')
                                },
                                route: ['/create-account'],
                                isDisabled: false
                            }] : []),

                        // VIEW EXISTING Joint APPLICANT
                        ...(isModuleAllowed({ value: "JointHolderCreation", compareArr: permissions, objKey: "permissonCode" }) ? [{
                            name: 'Onboarding Status',
                            activeIcon: <ActiveViewExistingIcon />,
                            inActiveIcon: <ViewExistingIcon />,
                            handleClick: () => {
                                dispatch(getParams(initialState))
                                history.push('/account-status')
                            },
                            route: ["/account-status"],
                            isDisabled: false
                        }] : []),

                    ],
                },
                //BANKS
                {
                    isSubMenuItem: true,
                    menuItem: {
                        name: 'BANKS',
                        handleClick: () => setActiveCategory(activeCategory === 'bankLinks' ? '' : 'bankLinks'),
                        dropdownId: 'bankLinks',
                        isDisabled: false
                    },
                    subMenuItem: [
                        // Add/Update Bank
                        ...([USER_ROLES.INVESTOR, USER_ROLES.RM].includes(role) &&
                            isModuleAllowed({ value: "EDITBANK", compareArr: permissions, objKey: "permissonCode" })? [{
                            name: 'Add/Update Bank',
                            activeIcon:  <ActiveAddNewIcon />,
                            inActiveIcon: <AddNewIcon />,
                            handleClick: () => {
                                history.push('/bank/updateBank', {
                                    // clientCode: investorClientId ?? ""
                                })
                            },
                            route: ["/bank/updateBank"],
                            isDisabled: false
                        }] : []),
                        // View bank Status
                        ...([USER_ROLES.INVESTOR, USER_ROLES.RM].includes(role) &&
                            isModuleAllowed({ value: "EDITBANK", compareArr: permissions, objKey: "permissonCode" })? [{
                            name: 'View Bank Status',
                            activeIcon: <ActiveViewExistingIcon />,
                            inActiveIcon: <ViewExistingIcon />,
                            handleClick: () => {
                                history.push('/bank/viewBank', {
                                    // clientCode: investorClientId ?? ""
                                })
                            },
                            route: ["/bank/viewBank"],
                            isDisabled: false
                        }] : [])

                    ],
                },

                //NOMINEE
                {
                    isSubMenuItem: true,
                    menuItem: {
                        name: 'NOMINEE',
                        handleClick: () => setActiveCategory(activeCategory === 'nomineeLinks' ? '' : 'nomineeLinks'),
                        dropdownId: 'nomineeLinks',
                        isDisabled: false
                    },
                    subMenuItem: [
                        // Add/Update Bank
                        ...([USER_ROLES.INVESTOR, USER_ROLES.RM].includes(role)&&
                            isModuleAllowed({ value: "EDITNOMINEE", compareArr: permissions, objKey: "permissonCode" }) ? [{
                            name: 'Add/Update Details',
                            activeIcon: <ActiveAddNewIcon />,
                            inActiveIcon: <AddNewIcon />,
                            handleClick: () => {
                                history.push('/nominee/updateNominee', {
                                    // clientCode: investorClientId ?? ""
                                })
                            },
                            route: ["/nominee/updateNominee"],
                            isDisabled: false
                        }] : []),


                    ],
                },

                // REPORTS
                {
                    isSubMenuItem: true,
                    menuItem: {
                        name: 'REPORTS',
                        handleClick: () => setActiveCategory(activeCategory === 'reports' ? '' : 'reports'),
                        dropdownId: 'reports',
                        isDisabled: false
                    },
                    subMenuItem: [
                        // NON INVESTOR REPORTS
                        ...((
                            ![USER_ROLES.INVESTOR].includes(role) &&
                            isModuleAllowed({ value: "INVReports", compareArr: permissions, objKey: "permissonCode" })
                        ) ? [{
                            name: 'Transaction Reports',
                            activeIcon: <ActiveDownloadIcon />,
                            inActiveIcon: <DownloadIcon />,
                            handleClick: () => {
                                dispatch(getParams(initialState))
                                history.push('/reports-download/')
                            },
                            route: [
                                '/reports-download/mf-client-statement',
                                '/reports-download/mf-transaction-wise-holding-report',
                                '/reports-download/holding-report',
                                '/reports-download/capital-gain-statement',
                                '/reports-download/transaction-history-report'
                            ],
                            isDisabled: false
                        }] : []),

                        // ONBOARDING REPORTS
                        ...(
                            (isModuleAllowed({ value: "OnboardReport", compareArr: permissions, objKey: "permissonCode" }))
                                ? [{
                                    name: 'Onboarding Reports',
                                    activeIcon: <ActiveDownloadIcon />,
                                    inActiveIcon: <DownloadIcon />,
                                    handleClick: () => {
                                        dispatch(getParams(initialState))
                                        history.push('/reports')
                                    },
                                    route: ["/reports"],
                                    isDisabled: false
                                }] : []),
                        ...((isModuleAllowed({ value: "DMDOVDReport", compareArr: permissions, objKey: "permissonCode" }))
                            ? [{
                                name: 'Deemed OVD Reports',
                                activeIcon: <ActiveViewExistingIcon />,
                                inActiveIcon: <ViewExistingIcon />,
                                handleClick: () => {
                                    dispatch(getParams(initialState))
                                    history.push('/ovd-reports')
                                },
                                route: ["/ovd-reports"],
                                isDisabled: false
                            }] : []),
                        ...((isModuleAllowed({ value: "SUBBROKERREPORT", compareArr: permissions, objKey: "permissonCode" }))
                            ? [{
                                name: 'Subbroker Reports',
                                activeIcon: <ActiveViewExistingIcon />,
                                inActiveIcon: <ViewExistingIcon />,
                                handleClick: () => {
                                    dispatch(getParams(initialState))
                                    history.push('/subbroker-reports')
                                },
                                route: ["/subbroker-reports"],
                                isDisabled: false
                            }] : [])
                    ],
                },

                //   ANALYTICS
                // {
                //     isSubMenuItem: true,
                //     menuItem: {
                //         name: 'ANALYTICS',
                //         handleClick: () => setActiveCategory(activeCategory === 'analytics' ? '' : 'analytics'),
                //         dropdownId: 'analytics',
                //         isDisabled: false
                //     },
                //     subMenuItem: [
                //         // MUTUAL FUND RESEARCH
                //         ...(isModuleAllowed({ value: "AnalyticsDashboard", compareArr: permissions, objKey: "permissonCode" }) ? [{
                //             name: 'Mutual Fund Research',
                //             activeIcon: <></>,
                //             inActiveIcon: <></>,
                //             handleClick: () => {
                //                 dispatch(getParams(initialState))
                //                 history.push('/analytics/mutual-fund-research')
                //             },
                //             route: ['/analytics/mutual-fund-research'],
                //             isDisabled: true
                //         }] : []),

                //         // PORTFOLIO ANALYTICS
                //         ...(isModuleAllowed({ value: "AnalyticsDashboard", compareArr: permissions, objKey: "permissonCode" }) ? [{
                //             name: 'Portfolio Analytics',
                //             activeIcon: <></>,
                //             inActiveIcon: <></>,
                //             handleClick: () => {
                //                 dispatch(getParams(initialState))
                //                 history.push('/analytics/portfolio-analytics')
                //             },
                //             route: ["/analytics/portfolio-analytics"],
                //             isDisabled: false
                //         }] : []),

                //         // REVENUE ANALYTICS
                //         ...(isModuleAllowed({ value: "AnalyticsDashboard", compareArr: permissions, objKey: "permissonCode" }) ? [{
                //             name: 'Revenue Analytics',
                //             activeIcon: <></>,
                //             inActiveIcon: <></>,
                //             handleClick: () => {
                //                 dispatch(getParams(initialState))
                //                 history.push('/analytics/revenue-analytics')
                //             },
                //             route: ["/analytics/revenue-analytics"],
                //             isDisabled: true
                //         }] : [])
                //     ],
                // },

                //   NON MF ORDERS
                // {
                //     isSubMenuItem: true,
                //     menuItem: {
                //         name: 'ORDERS',
                //         handleClick: () => setActiveCategory(activeCategory === 'orders' ? '' : 'orders'),
                //         dropdownId: 'orders',
                //         isDisabled: false
                //     },
                //     subMenuItem: [

                //         // NON MF ORDER DASHBOARD
                //         ...((ENABLE_ORDER_MANAGEMENT && isModuleAllowed({ value: "OrderDashboard", compareArr: permissions, objKey: "permissonCode" })) ? [{
                //             name: 'Order Dashboard',
                //             activeIcon: <ActiveViewExistingIcon />,
                //             inActiveIcon: <ViewExistingIcon />,
                //             handleClick: () => {
                //                 history.push('/orders/order-dashboard')
                //             },
                //             route: ['/orders/order-dashboard'],
                //             isDisabled: false
                //         }] : []),

                //         // NON MF ORDER ENTRY
                //         ...((ENABLE_ORDER_MANAGEMENT && isModuleAllowed({ value: "OrderEntry", compareArr: permissions, objKey: "permissonCode" })) ? [{
                //             name: 'Order Entry',
                //             activeIcon: <SidebarDashboardIcon />,
                //             inActiveIcon: <DashboardInactiveIcon />,
                //             handleClick: () => {
                //                 history.push('/orders/order-entry')
                //             },
                //             route: ["/orders/order-entry"],
                //             isDisabled: false
                //         }] : []),

                //         // ORDER APPROVALS
                //         ...((ENABLE_ORDER_MANAGEMENT && [USER_ROLES.AMCAPPROVER].includes(role)) ? [{
                //             name: 'Order Approvals',
                //             activeIcon: <SidebarDashboardIcon />,
                //             inActiveIcon: <DashboardInactiveIcon />,
                //             handleClick: () => {
                //                 history.push('/orders-approval')
                //             },
                //             route: ['/orders-approval'],
                //             isDisabled: [USER_ROLES.POACHECKER].includes(subRole)
                //         }] : [])
                //     ],
                // },

                // INVESTOR REPORTS
                {
                    isSubMenuItem: true,
                    menuItem: {
                        name: 'OTHERS',
                        handleClick: () => setActiveCategory(activeCategory === 'others' ? '' : 'others'),
                        dropdownId: 'others',
                        isDisabled: false
                    },
                    subMenuItem: [

                        // INVESTOR REPORTS
                        ...((
                            [USER_ROLES.INVESTOR].includes(role) &&
                            isModuleAllowed({ value: "INVReports", compareArr: permissions, objKey: "permissonCode" })
                        ) ? [{
                            name: 'Reports',
                            activeIcon: <InvestorActiveReportsIcon />,
                            inActiveIcon: <InvestorReportsIcon />,
                            handleClick: () => {
                                history.push('/investor-reports')
                            },
                            route: ['/investor-reports/client-statement'],
                            isDisabled: false
                        }] : []),
                    ],
                },

                // USER MANAGEMENT
                ...(isModuleAllowed({ value: "RML", compareArr: permissions, objKey: "permissonCode" }) ? [{
                    isSubMenuItem: false,
                    menuItem: {
                        name: 'User Management',
                        activeIcon: <></>,
                        inActiveIcon: <></>,
                        handleClick: () => history.push('/user-management'),
                        route: ['/user-management/relationship-managers'],
                        isDisabled: false
                    },
                    subMenuItem: [],
                }] : []),

                // ADMIN SUPPORT
                ...(USER_ROLES.AMC_ADMIN === role ? [{
                    isSubMenuItem: false,
                    menuItem: {
                        name: 'Admin Support',
                        activeIcon: <></>,
                        inActiveIcon: <></>,
                        handleClick: () => history.push('/admin-support'),
                        route: ['/admin-support'],
                        isDisabled: false
                    },
                    subMenuItem: [],
                }] : []),

                // RM MAPPING UPDATION
                ...(USER_ROLES.AMC_ADMIN === role ? [{
                    isSubMenuItem: false,
                    menuItem: {
                        name: 'RM Mapping Updation',
                        activeIcon: <></>,
                        inActiveIcon: <></>,
                        handleClick: () => history.push('/rm-mapping-change'),
                        route: ['/rm-mapping-change'],
                        isDisabled: false
                    },
                    subMenuItem: [],
                }] : []),

                // AMC ADMINS
                ...(USER_ROLES.SUPER_ADMIN === role ? [{
                    isSubMenuItem: false,
                    menuItem: {
                        name: 'AMC Admins',
                        activeIcon: <></>,
                        inActiveIcon: <></>,
                        handleClick: () => history.push('/amc-admins'),
                        route: ['/amc-admins'],
                        isDisabled: false
                    },
                    subMenuItem: [],
                }] : []),

                // CHECKER PAYMENT APPROVALS
                {
                    isSubMenuItem: true,
                    menuItem: {
                        name: 'PAYMENTS',
                        handleClick: () => setActiveCategory(activeCategory === 'payments' ? '' : 'payments'),
                        dropdownId: 'payments',
                        isDisabled: false
                    },
                    subMenuItem: [
                        // PAYMENT APPROVALS
                        ...((ENABLE_PAYMENT_CHECKER && [USER_ROLES.AMCAPPROVER].includes(role)) ? [{
                            name: 'Payment Approvals',
                            activeIcon: <PaymentsActiveIcon />,
                            inActiveIcon: <PaymentsInactiveIcon />,
                            handleClick: () => {
                                history.push('/payments-approval')
                            },
                            route: ['/payments-approval'],
                            isDisabled: [USER_ROLES.AMCAPPROVER].includes(role) && ![USER_ROLES.POACHECKER].includes(subRole)
                        }] : []),
                    ],
                }
            ]
        )
    }, [activeCategory, setActiveCategory])

    return sideBarData
}